import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import { startSsoAuthorization } from '@/api/auth.ts';
import FormikInput from '@/components/Formik/FormikInput.tsx';
import GlobalError from '@/components/FormikErrors/GlobalError.tsx';
import Button from '@/components/ui/Button/Button.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import OrganizationLoginLayout from '../OrganizationLoginLayout.tsx';
import useOrganizationLogin from '../useOrganizationLogin.ts';
import styles from './CHCLoginPage.module.scss';
import handleResponseErrors from '@/utils/handleResponseErrors.ts';
import { loginSchema } from '@/validations/auth.ts';

const CHCLoginPage = () => {
	const { data, isLoading, message, handleSsoClick } = useOrganizationLogin();
	const [isRedirecting, setIsRedirecting] = useState(false);

	useEffect(() => {
		const handlePageShow = () => setIsRedirecting(false);
		window.addEventListener('pageshow', handlePageShow);
		return () => window.removeEventListener('pageshow', handlePageShow);
	}, []);
	const microsoftProvider = data?.ssoProviders.find(provider => provider.id === 'microsoft');

	const formik = useFormik({
		initialValues: { email: '' },
		validationSchema: loginSchema.pick(['email']),
		onSubmit: (values, formikHelpers) => {
			try {
				setIsRedirecting(true);
				handleSsoClick();
				startSsoAuthorization(microsoftProvider!.action.url, { email: values.email.trim() });
			} catch (error) {
				setIsRedirecting(false);
				handleResponseErrors(error, formikHelpers);
			} finally {
				formikHelpers.setSubmitting(false);
			}
		},
	});

	if (isLoading) {
		return <Spinner />;
	}

	return (
		<OrganizationLoginLayout data={data} message={message} organizationLogoClassName={styles.organizationLogo}>
			<h2>Log into Goodwings</h2>
			<FormikProvider value={formik}>
				<Form noValidate>
					<GlobalError />
					<FormikInput
						name="email"
						type="email"
						aria-label="Email"
						autoComplete="username"
						placeholder="Email*"
						className={styles.formikInput}
						disabled={isRedirecting}
					/>
					<Button
						fullWidth
						type="submit"
						color="darkGreen"
						size="large"
						loading={isRedirecting}
						disabled={isRedirecting}
					>
						Login
					</Button>
				</Form>
			</FormikProvider>
		</OrganizationLoginLayout>
	);
};

export default CHCLoginPage;
